/**
 * useProductionMonitoring Hook
 * 
 * Custom React hook for Production Budget Monitoring data management and state
 */

import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import ProductionMonitoringApiService from '../services/productionMonitoringApiService';
import {
    ProductionBudgetDataRaw,
    ProductionBudgetRow,
    ProductionBudgetFilters,
} from '../types';
import { transformToDisplayRows } from '../utils';
import { PRODUCTION_MONITORING_DEFAULTS } from '../apiConfig';

/**
 * Hook return type
 */
interface UseProductionMonitoringReturn {
    rawProduction: ProductionBudgetDataRaw[];
    displayRows: ProductionBudgetRow[];
    loading: boolean;
    error: string | null;
    filters: ProductionBudgetFilters;
    setFilters: (filters: ProductionBudgetFilters) => void;
    fetchData: (filtersToUse?: ProductionBudgetFilters) => Promise<void>;
    totalCount: number;
}

/**
 * Get default filters with current month and year
 */
const getDefaultFilters = (): ProductionBudgetFilters => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    return {
        revision: PRODUCTION_MONITORING_DEFAULTS.REVISION,
        budget_year: year,
        budget_month: month,
        company_id: PRODUCTION_MONITORING_DEFAULTS.COMPANY_ID,
    };
};

/**
 * Custom hook for managing Production Monitoring data and state
 */
export const useProductionMonitoring = (): UseProductionMonitoringReturn => {
    const toast = useToast();

    const [rawProduction, setRawProduction] = useState<ProductionBudgetDataRaw[]>([]);
    const [displayRows, setDisplayRows] = useState<ProductionBudgetRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<ProductionBudgetFilters>(getDefaultFilters());
    const [totalCount, setTotalCount] = useState<number>(0);

    /**
     * Fetch Production Budget data from API
     * @param filtersToUse - Optional filters to use, defaults to current state filters
     */
    const fetchData = useCallback(async (filtersToUse?: ProductionBudgetFilters) => {
        setLoading(true);
        setError(null);

        const activeFilters = filtersToUse || filters;

        try {
            const response = await ProductionMonitoringApiService.getProductionBudgetReport(activeFilters);

            if (response.status === 'success') {
                setRawProduction(response.data);
                setDisplayRows(transformToDisplayRows(response.data, activeFilters.budget_month));
                setTotalCount(response.count);
            } else {
                throw new Error('Failed to fetch production budget data');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Gagal mengambil data budget produksi';
            setError(errorMessage);
            setRawProduction([]);
            setDisplayRows([]);
            setTotalCount(0);

            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setLoading(false);
        }
    }, [filters, toast]);

    return {
        rawProduction,
        displayRows,
        loading,
        error,
        filters,
        setFilters,
        fetchData,
        totalCount,
    };
};

export default useProductionMonitoring;
